import { useState, useEffect } from "react";
import { Clock, Check, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useChangeRequests } from "@/providers/ChangeRequestsProvider";
import { GetChangeRequestsResponse } from "@shared/types/payloads/GetChangeRequestsResponse";
import { ProjectOverview } from "./ProjectOverview";
import { RequestSection } from "./RequestSection";
import { CreateRequestDialog } from "./CreateRequestDialog";
import { RequestDetailDialog } from "./RequestDetailDialog";
import type { ChangeRequestsSectionProps } from "./types";

export function ChangeRequestsSection({
  serviceOrder,
  openNewRequest,
}: ChangeRequestsSectionProps) {
  const { toast } = useToast();
  const {
    changeRequests,
    isLoading,
    fetchChangeRequests,
    createChangeRequest,
    acceptChangeOrder,
  } = useChangeRequests();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<GetChangeRequestsResponse | null>(null);

  useEffect(() => {
    fetchChangeRequests(serviceOrder.id);
  }, [serviceOrder.id, fetchChangeRequests]);

  useEffect(() => {
    if (openNewRequest) {
      setCreateDialogOpen(true);
    }
  }, [openNewRequest]);

  const handleCreateRequest = async (type: string, changes: string[]) => {
    try {
      await createChangeRequest({
        serviceOrderId: serviceOrder.id,
        type,
        changes,
      });

      toast({
        title: "Request Submitted",
        description: "Your change request has been sent to the team",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to submit change request",
        variant: "destructive",
      });
      throw new Error("Failed to submit change request");
    }
  };

  const handleAcceptChangeOrder = async (
    request: GetChangeRequestsResponse
  ) => {
    const changeOrder = request.changeOrders[0];
    if (!changeOrder) return;

    try {
      const data = await acceptChangeOrder(request.id, changeOrder.id);

      if (data.signingUrl) {
        window.open(data.signingUrl, "_blank");
      }

      toast({
        title: "Success",
        description: "Change order accepted. Please sign the amended SOW.",
      });

      setDetailDialogOpen(false);
      fetchChangeRequests(serviceOrder.id);
    } catch {
      toast({
        title: "Error",
        description: "Failed to accept change order",
        variant: "destructive",
      });
    }
  };

  const handleSelectRequest = (request: GetChangeRequestsResponse) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const pendingRequests = changeRequests.filter((r) => r.status === "pending");
  const approvedRequests = changeRequests.filter(
    (r) => r.status === "approved"
  );
  const rejectedRequests = changeRequests.filter(
    (r) => r.status === "rejected"
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ProjectOverview
        serviceOrder={serviceOrder}
        totalRequests={changeRequests.length}
        pendingCount={pendingRequests.length}
        onNewRequest={() => setCreateDialogOpen(true)}
      />

      <RequestSection
        title="Pending Review"
        icon={Clock}
        iconColor="text-amber-400"
        requests={pendingRequests}
        emptyMessage="No pending requests"
        onSelectRequest={handleSelectRequest}
      />

      <RequestSection
        title="Approved"
        icon={Check}
        iconColor="text-emerald-400"
        requests={approvedRequests}
        emptyMessage="No approved requests yet"
        onSelectRequest={handleSelectRequest}
      />

      <RequestSection
        title="Rejected"
        icon={X}
        iconColor="text-red-400"
        requests={rejectedRequests}
        emptyMessage="No rejected requests"
        onSelectRequest={handleSelectRequest}
      />

      <CreateRequestDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateRequest}
      />

      <RequestDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        request={selectedRequest}
        onAcceptChangeOrder={handleAcceptChangeOrder}
      />
    </div>
  );
}
